import { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Loader2, ShieldAlert } from "lucide-react";
import { toast } from "sonner";

const ACTION_STYLE = {
  create: "bg-emerald-100 text-emerald-700",
  update: "bg-amber-100 text-amber-700",
  delete: "bg-red-100 text-red-700",
  annule: "bg-red-100 text-red-700",
  encaissement: "bg-blue-100 text-blue-700",
  transfert: "bg-indigo-100 text-indigo-700",
  import: "bg-purple-100 text-purple-700",
};

const ENTITY_LABEL = {
  facture_client: "Facture de vente", facture_fournisseur: "Facture fournisseur",
  caisse_mouvement: "Caisse", banque_mouvement: "Banque", depense: "Dépense",
  client: "Client", fournisseur: "Fournisseur", bl_client: "Bon de livraison",
  bon_commande: "Bon de commande", produit: "Produit", inventaire: "Inventaire",
  production: "Production", societe: "Société", user: "Utilisateur", caisse: "Caisse",
};

function fmtTs(ts) {
  if (!ts) return "—";
  try { return new Date(ts).toLocaleString("fr-FR"); } catch { return ts; }
}

export default function Audit() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/audit", { params: { limit: 300 } });
      setItems(data.items); setTotal(data.total);
    } catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const allowed = user && (user.role === "administrateur" || user.role === "direction");
  if (!allowed) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-center" data-testid="audit-forbidden">
        <ShieldAlert className="h-10 w-10 text-muted-foreground mb-3" />
        <p className="text-lg font-heading">Accès réservé</p>
        <p className="text-sm text-muted-foreground">Le journal d'activité est réservé aux profils Administrateur et Direction.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="audit-page">
      <PageHeader title="Journal d'activité" subtitle={`${total} opération(s) enregistrée(s) — qui a créé, modifié ou supprimé`} testid="audit-header" />
      <div className="rounded-xl border border-border bg-card overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader><TableRow className="bg-muted/50"><TableHead>Date</TableHead><TableHead>Utilisateur</TableHead><TableHead>Action</TableHead><TableHead>Type</TableHead><TableHead>Détails</TableHead></TableRow></TableHeader>
              <TableBody>
                {items.length === 0 ? (
                  <TableRow><TableCell colSpan={5} className="text-center py-10 text-muted-foreground">Aucune activité enregistrée.</TableCell></TableRow>
                ) : items.map((a) => (
                  <TableRow key={a.id} data-testid={`audit-row-${a.id}`}>
                    <TableCell className="tabular text-sm whitespace-nowrap">{fmtTs(a.timestamp)}</TableCell>
                    <TableCell className="text-sm font-medium">{a.user_name || "—"}</TableCell>
                    <TableCell><Badge className={ACTION_STYLE[a.action] || "bg-muted text-foreground"}>{a.action}</Badge></TableCell>
                    <TableCell className="text-sm">{ENTITY_LABEL[a.entity] || a.entity}</TableCell>
                    <TableCell className="text-sm text-muted-foreground max-w-[360px] truncate">{a.new ? (a.new.numero || JSON.stringify(a.new).slice(0, 80)) : (a.entity_id || "—")}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </div>
  );
}
